import { useTheme } from "../../../hooks/ThemeContext";
import React, { useState, useEffect } from "react";
import { useDineCentralAuth } from "../../../hooks/useDineCentralAuth";
import 'react-toastify/dist/ReactToastify.css';
import PaymentDetails from "./PaymentOptions/PaymentDetails";
import { useStripe } from "@stripe/react-stripe-js";
import ClipLoader from "react-spinners/ClipLoader";
import TimeService from "../../../service/TimeService";
import ToastService from "../../../service/ToastService";
import { Tooltip } from "react-tooltip";
import { Link } from "react-router-dom";
import PhoneNumberInput from "./PhoneNumberInput";
import { getRemoteEnv } from '../../../config/remoteEnv';
import { httpsCallable } from "firebase/functions";
import { DINECENTRAL_FUNCTIONS } from "../../../Firebase.dinecentral";

type Props = {
  order: any;
  amount: number;
  tipAmount: number;
  tableNumber: number | null;
  onBack?: any;
  onSuccessReturn: any;
  setCart: any;
  menuSettings: any[];
  productData: any[];
  updateQuantity: any;
  removeOption: any;
  openingHours: any;
  orderType: string;
  deliveryAddress: any;
  deliveryQuote: any;
  testMode: boolean;
};

const PaymentDetailsComponent = (props: Props) => {
  const { theme } = useTheme();
  const auth = useDineCentralAuth();
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [canMakePayment, setCanMakePayment] = useState<boolean | null>(null);
  const [paymentRequest, setPaymentRequest] = useState<any>(null);
  const stripe = useStripe();
  
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: auth.user?.email ?? "",
  });

  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [agreeToMarketing, setAgreeToMarketing] = useState(false);
  // UK soft opt-in: the refusal box (PECR reg 22(3) requires a chance to
  // refuse AT collection). Ticked = explicit opt-out recorded via the order.
  const [marketingRefused, setMarketingRefused] = useState(false);

  // Marketing regime by STORE country: US = CAN-SPAM opt-out (disclosure,
  // auto-opt-in downstream unless previously opted out), UK = PECR soft
  // opt-in (disclosure + refusal box), everywhere else = strict opt-in.
  const marketingRegime = ((): "us" | "uk" | "strict" => {
    const c = (getRemoteEnv().countryCode || "").trim().toUpperCase();
    if (c === "US" || c === "USA" || c === "UNITED STATES") return "us";
    if (c === "GB" || c === "UK" || c === "UNITED KINGDOM") return "uk";
    return "strict";
  })();
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  useEffect(() => {
    const userEmail = auth.user?.email;
    if (userEmail) {
      setFormData(prev => ({ ...prev, email: userEmail }));
      setAgreeToTerms(true);
    }
    const customerName = auth.user?.displayName;
    if (customerName) {
      setFormData(prev => ({ ...prev, name: customerName }));
    }
    const customerPhone = auth.user?.phoneNumber;
    if (customerPhone) {
      setFormData(prev => ({ ...prev, phone: customerPhone }));
      setIsPhoneValid(true);
    }
  }, [auth.user]);

  React.useEffect(() => {
    const checkCanMakePayment = async () => {
      if (!stripe) return;
      const pr = stripe.paymentRequest({
        country: getRemoteEnv().countryCode || 'GB',
        currency: (getRemoteEnv().currencyCode || "gbp").toLowerCase(),
        total: {
          label: 'Total',
          amount: Math.round(props.amount * 100),
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });
      const result = await pr.canMakePayment();
      setPaymentRequest(pr);
      setCanMakePayment(!!result);
    };
    checkCanMakePayment();
  }, [stripe, props.amount]);

  const validatePForm = () => {
    let errors: string[] = [];
    let valid = true;
    if (!formData.name) { errors.push("Name is required."); valid = false; }
    if (!isPhoneValid || !formData.phone) { errors.push("Enter a valid phone number for your selected country."); valid = false; }
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) { errors.push("Enter a valid email address."); valid = false; }
    if (!agreeToTerms) { errors.push("Agreement to terms is required."); valid = false; }
    setFormErrors(errors);
    return valid;
  };

  // ── Call the appropriate marketing cloud function ────────────────────────────
  const submitMarketingConsent = async () => {
    if (!agreeToMarketing) return;

    const appId = getRemoteEnv().appId;

    try {
      if (auth.user) {
        // Authenticated user — use the auth-gated callable
        const setMarketingPermissions = httpsCallable(DINECENTRAL_FUNCTIONS, "setMarketingPermissions");
        await setMarketingPermissions({ appId, agreeToMarketing: true });
        auth.updateMarketingPreference(true);
      } else {
        // Guest user — use the unauthenticated callable
        const setGuestMarketingPermissions = httpsCallable(DINECENTRAL_FUNCTIONS, "setGuestMarketingPermissions");
        await setGuestMarketingPermissions({
          email: formData.email,
          appId,
          agreeToMarketing: true,
          name: formData.name || null,
        });
      }
    } catch (error) {
      // Non-blocking — log but don't prevent payment from proceeding
      console.error("Error submitting marketing consent:", error);
    }
  };

  const onPersonalInfoCheck = async () => {
    if (props.order.length > 0) {
      let allowedMenus = await TimeService.getAllowedMenus(props.menuSettings);
      let updatedCart = false;

      for (let orderItem of props.order) {
        let itemObj = props.productData.filter((x) => x.productId === orderItem.id)[0];
        let menuFound = false;
        for (let menuItem of orderItem.menus) {
          if (allowedMenus.includes(menuItem)) {
            let categoryObj = props.menuSettings.filter(x => x.menuName === menuItem)[0].categories.filter((c: any) => c.id === itemObj.category)[0];
            if (categoryObj.enabled !== false && !categoryObj.deleted) {
              menuFound = true;
            }
          }
        }
        if (menuFound == false || itemObj.isDeleted || itemObj.isDisabled || itemObj.outOfStock) {
          updatedCart = true;
          props.updateQuantity(orderItem, 0);
        }

        if (orderItem.additionalOptionIds && orderItem.additionalOptionIds.length > 0) {
          for (let addOption of orderItem.additionalOptionIds) {
            let additionalOptionObj = props.productData.filter((x) => x.productId === addOption)[0];
            if (!additionalOptionObj.outOfStock && !additionalOptionObj.isDisabled && !additionalOptionObj.isDeleted) {
              let addOptionsCategoryObj = props.menuSettings.filter(x => x.menuName === additionalOptionObj.menus[0])[0].categories.filter((c: any) => c.id === additionalOptionObj.category)[0];
              if (addOptionsCategoryObj.enabled !== false && !addOptionsCategoryObj.deleted) {
                ;
              } else {
                props.removeOption(orderItem, addOption);
                updatedCart = true;
              }
            } else {
              props.removeOption(orderItem, addOption);
              updatedCart = true;
            }
          }
        }
      }
      
      if (!updatedCart) {
        if (!validatePForm()) return;

        // Fire and forget — never block payment on marketing consent
        submitMarketingConsent();
        setShowPaymentMethods(true);
      } else {
        props.onBack();
        ToastService.showToast(
          `Unavailable items have been removed from your cart. Please check cart before proceeding!`,
          {
            style: {
              backgroundColor: "darkred",
              color: "white",
              minHeight: "80px",
              borderRadius: "15px",
            },
          }
        );
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'email' && auth.user?.email) return;
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (formattedNumber: string, isValid: boolean) => {
    setFormData({ ...formData, phone: formattedNumber });
    setIsPhoneValid(isValid);
  };

  const styles = {
    container: {
      display: "flex",
      flexDirection: "column" as const,
      height: "100%",
      color: theme.cartTextColor,
      padding: "20px",
      boxSizing: "border-box" as const,
      overflowY: "auto" as const,
      gap: "16px",
    },
    backButton: {
      width: "auto",
      padding: "8px 16px",
      cursor: "pointer",
      backgroundColor: theme.cartButtonColor,
      color: theme.cartButtonTextColor,
      border: "none",
      borderRadius: "10px",
      fontSize: "14px",
      fontWeight: "500",
      alignSelf: "flex-start",
      transition: "transform 0.2s ease",
    },
    formContainer: {
      display: "flex",
      flexDirection: "column" as const,
      gap: "20px",
      flex: 1,
    },
    title: {
      fontSize: "24px",
      fontWeight: 800,
      letterSpacing: "-0.4px",
      margin: "0",
      color: theme.cartTextColor,
    },
    subtitle: {
      fontSize: "13.5px",
      lineHeight: 1.5,
      opacity: 0.62,
      margin: "-12px 0 0",
      color: theme.cartTextColor,
    },
    formGroup: {
      display: "flex",
      flexDirection: "column" as const,
      gap: "8px",
    },
    label: {
      fontSize: "12px",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase" as const,
      opacity: 0.6,
      color: theme.cartTextColor,
    },
    input: {
      height: "46px",
      width: "100%",
      padding: "0 14px",
      border: `1.5px solid ${theme.cartSummaryBorder || theme.searchBarColor || "#e1e1e1"}`,
      borderRadius: "14px",
      background: theme.searchBarColor || "none",
      color: theme.cartTextColor,
      fontSize: "16px",
      fontFamily: "inherit",
      boxSizing: "border-box" as const,
      transition: "border-color 0.2s ease",
    },
    checkboxSection: {
      display: "flex",
      flexDirection: "column" as const,
      gap: "12px",
    },
    checkboxContainer: {
      display: "flex",
      alignItems: "flex-start",
      gap: "8px",
    },
    checkbox: {
      width: "20px",
      height: "20px",
      marginTop: "2px",
      cursor: "pointer",
    },
    checkboxLabel: {
      fontSize: "14px",
      lineHeight: "1.5",
      color: theme.cartTextColor,
    },
    link: {
      color: theme.cartButtonColor,
      textDecoration: "none",
      fontWeight: "500",
    },
    nextButton: {
      height: "52px",
      width: "100%",
      cursor: "pointer",
      backgroundColor: theme.payButtonColor,
      color: theme.payButtonTextColor,
      border: "none",
      borderRadius: "14px",
      fontSize: "15px",
      fontWeight: 700,
      fontFamily: "inherit",
      marginTop: "auto",
      transition: "transform 0.2s ease",
    },
    errorList: {
      backgroundColor: "rgba(229, 72, 77, 0.08)",
      border: "1px solid rgba(229, 72, 77, 0.25)",
      padding: "14px 16px",
      borderRadius: "14px",
      marginTop: "4px",
    },
    errorItem: {
      color: "#e5484d",
      fontSize: "13.5px",
      marginBottom: "6px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    loader: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
    }
  };

  if (!showPaymentMethods) {
    return (
      <div style={styles.container}>
        <button style={styles.backButton} onClick={props.onBack}>Back</button>

        <div style={styles.formContainer}>
          <h2 style={styles.title}>Your details</h2>
          <p style={styles.subtitle}>We'll only use these to keep you updated about your order.</p>

          <div style={styles.formGroup}>
            <label style={styles.label}>Name</label>
            <input
              name="name"
              value={formData.name}
              onChange={handleChange}
              style={{
                ...styles.input,
                backgroundColor: auth.user?.displayName ? theme.searchBarColor || '#f0f0f0' : (theme.searchBarColor || "none"),
                cursor: auth.user?.displayName ? 'not-allowed' : 'text',
                opacity: auth.user?.displayName ? 0.7 : 1,
              }}
              placeholder="Enter your full name"
              disabled={!!auth.user?.displayName}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Email Address</label>
            <input
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={{
                ...styles.input,
                backgroundColor: auth.user?.email ? theme.searchBarColor || '#f0f0f0' : (theme.searchBarColor || "none"),
                cursor: auth.user?.email ? 'not-allowed' : 'text',
                opacity: auth.user?.email ? 0.7 : 1,
              }}
              placeholder="Enter your email address"
              disabled={!!auth.user?.email}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Phone Number{" "}
              <span
                data-tooltip-id="phone-tip"
                data-tooltip-content="To allow us to contact you about your order if necessary"
              >
                &#9432;
              </span>
            </label>
            <PhoneNumberInput
              value={formData.phone}
              onChange={handlePhoneChange}
              defaultCountryCode={getRemoteEnv().countryCode || 'GB'}
              theme={theme}
              disabled={!!auth.user?.phoneNumber}
              placeholder="Enter phone number"
            />
          </div>

          <div style={styles.checkboxSection}>
            {!auth.user?.emailVerified && (
              <div style={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  style={styles.checkbox}
                />
                <label htmlFor="agreeToTerms" style={styles.checkboxLabel}>
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" style={styles.link}>
                    Terms of Service
                  </Link>
                  {" "}and{" "}
                  <Link to="/privacyPolicy" target="_blank" rel="noopener noreferrer" style={styles.link}>
                    Privacy Policy
                  </Link>
                </label>
              </div>
            )}

            {/* Marketing consent, by store country.
                strict: opt-in checkbox (hidden once opted in) — as always.
                us: disclosure; the box returns ONLY for accounts that
                    explicitly opted out (the willing opt-back-in path).
                uk: disclosure + the soft-opt-in refusal box. */}
            {marketingRegime === "strict" && (!auth.user || !auth.agreeToMarketing) && (
              <div style={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  id="marketing"
                  checked={agreeToMarketing}
                  onChange={(e) => setAgreeToMarketing(e.target.checked)}
                  style={styles.checkbox}
                />
                <label htmlFor="marketing" style={styles.checkboxLabel}>
                  I agree to receiving promotions and offers by email
                </label>
              </div>
            )}
            {marketingRegime === "us" && auth.user && auth.agreeToMarketing === false && (
              <div style={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  id="marketing"
                  checked={agreeToMarketing}
                  onChange={(e) => setAgreeToMarketing(e.target.checked)}
                  style={styles.checkbox}
                />
                <label htmlFor="marketing" style={styles.checkboxLabel}>
                  I agree to receiving promotions and offers by email
                </label>
              </div>
            )}
            {marketingRegime === "us" && !(auth.user && auth.agreeToMarketing === false) && (
              <div style={{ fontSize: "12px", opacity: 0.7, lineHeight: 1.5, margin: "6px 0" }}>
                By placing your order, you'll receive {getRemoteEnv().appName || "our"} offers and
                seasonal menus by email. Unsubscribe anytime.
              </div>
            )}
            {marketingRegime === "uk" && (
              <>
                <div style={{ fontSize: "12px", opacity: 0.7, lineHeight: 1.5, margin: "6px 0" }}>
                  We'll email you {getRemoteEnv().appName || "our"} offers and seasonal menus —
                  unsubscribe anytime.
                </div>
                <div style={styles.checkboxContainer}>
                  <input
                    type="checkbox"
                    id="marketingRefusal"
                    checked={marketingRefused}
                    onChange={(e) => setMarketingRefused(e.target.checked)}
                    style={styles.checkbox}
                  />
                  <label htmlFor="marketingRefusal" style={styles.checkboxLabel}>
                    Tick here if you'd rather not receive these emails
                  </label>
                </div>
              </>
            )}
          </div>

          {formErrors.length > 0 && (
            <div style={styles.errorList}>
              {formErrors.map((error, index) => (
                <div key={index} style={styles.errorItem}>• {error}</div>
              ))}
            </div>
          )}

          <button style={styles.nextButton} onClick={onPersonalInfoCheck}>
            Continue to Payment
          </button>
        </div>
        <Tooltip id="phone-tip" />
      </div>
    );
  } else {
    if (canMakePayment != null && paymentRequest) {
      return (
        <PaymentDetails
          stripe={stripe}
          paymentRequest={paymentRequest}
          canMakePayment={canMakePayment}
          agreeToMarketing={agreeToMarketing}
          marketingRefused={marketingRefused}
          order={props.order}
          setCart={props.setCart}
          amount={props.amount}
          tipAmount={props.tipAmount}
          tableNumber={props.tableNumber}
          formData={formData}
          onBack={() => setShowPaymentMethods(false)}
          onSuccessReturn={() => props.onSuccessReturn()}
          skipMethodsScreen={canMakePayment == false}
          updateQuantity={props.updateQuantity}
          removeOption={props.removeOption}
          productData={props.productData}
          menuSettings={props.menuSettings}
          openingHours={props.openingHours}
          orderType={props.orderType}
          deliveryAddress={props.deliveryAddress}
          deliveryQuote={props.deliveryQuote}
          testMode={props.testMode}
        />
      );
    } else {
      return (
        <div style={styles.loader}>
          <ClipLoader color={theme.cartButtonColor} loading={true} size={24} />
        </div>
      );
    }
  }
};

export default PaymentDetailsComponent;